import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import ArtworkCard from "@/components/ArtworkCard";
import { useArtworks } from "@/hooks/useArtworks";

interface Artwork {
  id: string;
  title: string;
  category: string;
  format: string;
  canvas: string;
  medium: string;
  size: string;
  price: number;
  image: string;
  description: string;
  seo_description?: string;
  technique: string;
  inspiration: string;
  year: number;
  status: string;
  tags: string[];
  vk_link: string;
  website_link: string;
}

const FeaturedCarousel = () => {
  const { artworks, loading } = useArtworks();
  const [randomArtworks, setRandomArtworks] = useState<Artwork[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Get all artworks (featured + gallery) when data is loaded
  useEffect(() => {
    if (!loading && artworks.featured && artworks.gallery) {
      const allArtworks = [...artworks.featured, ...artworks.gallery];
      
      // Shuffle and select 6 random artworks
      const shuffled = [...allArtworks].sort(() => Math.random() - 0.5);
      setRandomArtworks(shuffled.slice(0, 6));
    }
  }, [loading, artworks]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, randomArtworks.length - 2));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? Math.max(0, randomArtworks.length - 3) : prev - 1
    );
  };

  // Show 3 artworks at a time
  const visibleArtworks = randomArtworks.slice(currentIndex, currentIndex + 3);

  if (loading) {
    return <div className="text-center py-8">Загрузка...</div>;
  }

  return (
    <div className="relative">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {visibleArtworks.map((artwork) => (
          <ArtworkCard key={artwork.id} {...artwork} />
        ))}
      </div>

      {/* Navigation buttons */}
      {randomArtworks.length > 3 && (
        <div className="flex justify-center gap-4 mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={prevSlide}
            disabled={currentIndex === 0}
            className="w-10 h-10 p-0"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <div className="flex items-center gap-2">
            {Array.from({ length: Math.max(1, randomArtworks.length - 2) }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={nextSlide}
            disabled={currentIndex >= randomArtworks.length - 3}
            className="w-10 h-10 p-0"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default FeaturedCarousel;